import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence, animate } from "motion/react";
import NumberFlow from "@number-flow/react";
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import {
    DollarSign, CalendarDays, Megaphone, FilePenLine, AlertTriangle,
    Globe, ChevronDown, FileText, Clock, TrendingUp, Lock, Wifi,
    RotateCcw, Bell, CalendarCheck2, CalendarX, Image, Video, Users,
    ShieldCheck, Eye, Timer, ArrowUpRight, ArrowDownRight, Minus, Zap,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// SPORTS ENDORSEMENT DEAL — SportsDrinkCo × Athlete
// ─────────────────────────────────────────────────────────────────────────────

const CONTRACT = {
    identity: {
        type: "Athlete Endorsement Agreement",
        brand: "SportsDrinkCo",
        category: "Energy Drink",
        governingLaw: "State of New York",
        status: "Under Review",
        version: "Draft v3",
        effectiveDate: "Apr 1, 2026",
    },

    economics: {
        guaranteed: 150_000,
        totalPotential: 180_000,
        priorDraftTotal: 150_000,
        paymentSchedule: [
            { label: "Signing Fee",       amount: 75_000, due: "On execution",         type: "Guaranteed" as const },
            { label: "Content Fee — Q2",  amount: 22_500, due: "Jul 1, 2026",          type: "Guaranteed" as const },
            { label: "Content Fee — Q4",  amount: 22_500, due: "Oct 1, 2026",          type: "Guaranteed" as const },
            { label: "Royalty (est.)",    amount: 30_000, due: "Quarterly, per sales", type: "Variable" as const   },
            { label: "Performance Bonus", amount: 30_000, due: "On qualifying event",  type: "Conditional" as const },
        ],
        areaData: [
            { month: "Apr", value: 75_000  }, { month: "May", value: 90_000  },
            { month: "Jun", value: 98_000  }, { month: "Jul", value: 120_500 },
            { month: "Aug", value: 128_000 }, { month: "Sep", value: 135_000 },
            { month: "Oct", value: 157_500 }, { month: "Nov", value: 163_000 },
            { month: "Dec", value: 180_000 },
        ],
    },

    term: {
        startDate: "Apr 1, 2026",
        endDate: "Mar 31, 2027",
        duration: "12 months",
        renewalOptions: "2 × 12-month options",
        noticePeriod: "90 days prior to end date",
        keyDates: [
            { label: "Effective Date",   date: "Apr 1, 2026",  note: "Agreement takes effect",              icon: "CalendarCheck2" },
            { label: "Campaign Launch",  date: "Apr 15, 2026", note: "Content goes live",                   icon: "Megaphone"      },
            { label: "Notice Deadline",  date: "Jan 1, 2027",  note: "90-day renewal/exit window opens",    icon: "Bell"           },
            { label: "Agreement Expiry", date: "Mar 31, 2027", note: "End of initial term",                 icon: "CalendarX"      },
        ],
    },

    rights: {
        territory: "United States and Canada",
        exclusivity: "Energy drink & sports drink category only",
        channels: ["Instagram", "TikTok", "YouTube Shorts", "In-store displays"],
        approvalProcess: "Brand approves all content min. 5 business days prior",
        postTermUsage: "6 months post-term for already-published content",
        channelBreakdown: [
            { channel: "Instagram",      rights: "Sponsored posts + Stories + Reels", paid: true  },
            { channel: "TikTok",         rights: "Organic-style sponsored videos",    paid: true  },
            { channel: "YouTube Shorts", rights: "Licensed clips — no direct ads",    paid: false },
            { channel: "In-store",       rights: "POS displays, signage, sampling",   paid: false },
        ],
    },

    obligations: [
        { deliverable: "3 Instagram posts",        due: "Monthly ~1/month",      note: "Must include brand hashtag + tag", icon: "Image"       },
        { deliverable: "1 TikTok video (30–60s)",  due: "Q2 2026 by Jun 30",     note: "Organic-style, no hard sell",      icon: "Video"       },
        { deliverable: "2 in-person activations",  due: "Dates TBD by Brand",    note: "Travel & accommodation covered",   icon: "Users"       },
        { deliverable: "Pre-approval of content",  due: "5 business days prior", note: "Via brand portal — mandatory",     icon: "ShieldCheck" },
    ],

    clauses: [
        { title: "Morals & Conduct",      summary: "Brand may terminate immediately if Athlete is convicted of a crime, engages in conduct widely deemed offensive, or takes public positions materially adverse to Brand's reputation. Determination at Brand's sole discretion.", flag: "Broad subjective discretion — recommend narrowing trigger language." },
        { title: "Exclusivity",           summary: "During the term, Athlete may not endorse or appear in advertising for any energy, sports, or hydration product other than Brand's contracted line. Non-beverage categories are unrestricted.", flag: null },
        { title: "IP & Name / Image",     summary: "Athlete grants Brand a non-exclusive (within category) license to use Athlete's name, approved likeness, and voice across contracted channels during the term and for 6 months post-term for already-published content.", flag: "Post-term window of 6 months — confirm acceptable." },
        { title: "Termination",           summary: "Either party may terminate with 60 days written notice. Brand may terminate immediately for morals clause breach. Athlete forfeits unpaid compensation upon termination for cause.", flag: "Asymmetric immediacy favors Brand on for-cause termination." },
        { title: "Indemnification",       summary: "Mutual indemnification for third-party claims arising from each party's own breach, misrepresentation, or negligence. Standard carve-outs for IP infringement.", flag: null },
        { title: "Liability Cap",         summary: "Neither party's aggregate liability may exceed the total amounts actually paid to Athlete at the time of the claim.", flag: null },
    ],

    redlineHistory: [
        { clause: "Guaranteed Fee",      from: "$120,000",         to: "$150,000",              delta: "+$30K",      positive: true,  timestamp: "Mar 24 · 2:32 PM",  author: "Label Counsel",   initials: "LC" },
        { clause: "Territory",           from: "US only",           to: "US and Canada",         delta: "Expanded",   positive: true,  timestamp: "Mar 22 · 10:15 AM", author: "Athlete Manager", initials: "AM" },
        { clause: "Exclusivity Scope",   from: "All beverages",     to: "Energy drinks only",    delta: "Narrowed",   positive: false, timestamp: "Mar 20 · 4:48 PM",  author: "Label Counsel",   initials: "LC" },
        { clause: "Bonus Kicker",        from: "None",              to: "$30,000",               delta: "Added",      positive: true,  timestamp: "Mar 18 · 9:22 AM",  author: "Athlete Manager", initials: "AM" },
        { clause: "Notice Period",       from: "60 days",           to: "90 days",               delta: "+30 days",   positive: true,  timestamp: "Mar 14 · 11:05 AM", author: "Label Counsel",   initials: "LC" },
        { clause: "Post-Term Window",    from: "3 months",          to: "6 months",              delta: "+3 months",  positive: false, timestamp: "Mar 10 · 3:17 PM",  author: "Label Counsel",   initials: "LC" },
        { clause: "Approval Timeline",   from: "3 business days",   to: "5 business days",       delta: "+2 days",    positive: false, timestamp: "Mar 6 · 9:44 AM",   author: "Brand Counsel",   initials: "BC" },
        { clause: "Initial Term",        from: "6 months",          to: "12 months",             delta: "+6 months",  positive: true,  timestamp: "Feb 28 · 2:01 PM",  author: "Athlete Manager", initials: "AM" },
    ],
};

// Animation
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 280, damping: 26 } },
};

// Icon map
const ICON_MAP: Record<string, React.ReactNode> = {
    CalendarCheck2: <CalendarCheck2 className="h-4 w-4" />,
    Megaphone:      <Megaphone      className="h-4 w-4" />,
    Bell:           <Bell           className="h-4 w-4" />,
    CalendarX:      <CalendarX      className="h-4 w-4" />,
    Image:          <Image          className="h-4 w-4" />,
    Video:          <Video          className="h-4 w-4" />,
    Users:          <Users          className="h-4 w-4" />,
    ShieldCheck:    <ShieldCheck    className="h-4 w-4" />,
};

const TYPE_CLS = {
    Guaranteed:  "bg-emerald-500/15 text-emerald-300",
    Variable:    "bg-blue-500/15    text-blue-300",
    Conditional: "bg-amber-500/15   text-amber-300",
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

function CardShell({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`rounded-[2rem] bg-neutral-800 border border-white/5 overflow-hidden ${className}`}>
            {children}
        </div>
    );
}

function CardHeader({ title, icon, action }: { title: string; icon: React.ReactNode; action?: React.ReactNode }) {
    return (
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-white/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#547bb5] to-[#6e53b8] text-white">
                {icon}
            </div>
            <h3 className="flex-1 text-sm font-semibold text-white">{title}</h3>
            {action}
        </div>
    );
}

function DataRow({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3 border-b border-white/5 py-2.5 last:border-0">
            {icon && <div className="mt-0.5 shrink-0 text-white/40">{icon}</div>}
            <span className="shrink-0 text-sm text-white/50">{label}</span>
            <span className="ml-auto text-right text-sm font-medium text-white">{value}</span>
        </div>
    );
}

function Divider() { return <div className="my-4 h-px bg-white/8" />; }

function FlipBtn({ onClick, label = "Details" }: { onClick?: () => void; label?: string }) {
    return (
        <button onClick={onClick}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-white/50 transition-colors hover:bg-white/10 hover:text-white">
            <RotateCcw className="h-3 w-3" /> {label}
        </button>
    );
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-white/10 bg-neutral-900 px-3 py-2 text-sm shadow-xl">
            <div className="font-semibold text-white">{label}</div>
            <div className="text-white/50">${(payload[0].value / 1000).toFixed(1)}K cumulative</div>
        </div>
    );
}

// FLIP CARD
function FlipCard({ front, back }: { front: React.ReactNode; back: React.ReactNode }) {
    const [flipped, setFlipped] = useState(false);
    const frontRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);
    useEffect(() => {
        if (!frontRef.current) return;
        const ro = new ResizeObserver(() => { if (frontRef.current) setCardHeight(frontRef.current.offsetHeight); });
        ro.observe(frontRef.current);
        return () => ro.disconnect();
    }, []);
    return (
        <div style={{ perspective: "1400px", minHeight: cardHeight }}>
            <motion.div animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformStyle: "preserve-3d", position: "relative", minHeight: "inherit" }}>
                <div ref={frontRef} style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
                    {React.cloneElement(front as React.ReactElement<{ onFlip?: () => void }>, { onFlip: () => setFlipped(true) })}
                </div>
                <div style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)", position: "absolute", top: 0, left: 0, right: 0, minHeight: "100%" }}>
                    {React.cloneElement(back as React.ReactElement<{ onFlip?: () => void }>, { onFlip: () => setFlipped(false) })}
                </div>
            </motion.div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MONEY CARD
// ─────────────────────────────────────────────────────────────────────────────

function MoneyFront({ onFlip, mounted }: { onFlip?: () => void; mounted: boolean }) {
    return (
        <CardShell>
            <CardHeader title="Money You'll Receive" icon={<DollarSign className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Schedule" />} />
            <div className="px-5 py-4">
                <div className="mb-4 rounded-2xl border border-white/5 bg-neutral-700 p-4">
                    <div className="text-xs text-white/40">Total Deal Value</div>
                    <div className="mt-1 text-5xl font-black tracking-tight text-white">
                        {mounted ? (
                            <NumberFlow value={CONTRACT.economics.totalPotential / 1000}
                                format={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }} suffix="K" />
                        ) : "$180K"}
                    </div>
                    <div className="mt-2 text-xs font-semibold text-emerald-400">
                        +${((CONTRACT.economics.totalPotential - CONTRACT.economics.priorDraftTotal) / 1000).toFixed(0)}K vs. prior draft
                    </div>
                </div>
                <div className="mb-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-white/5 bg-neutral-700 p-3">
                        <div className="text-xs text-white/40">Guaranteed</div>
                        <div className="mt-1 text-2xl font-bold text-white">
                            {mounted ? <NumberFlow value={CONTRACT.economics.guaranteed / 1000}
                                format={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }} suffix="K" /> : "$150K"}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-neutral-700 p-3">
                        <div className="text-xs text-white/40">Bonus Kicker</div>
                        <div className="mt-1 text-2xl font-bold text-white">
                            {mounted ? <NumberFlow value={(CONTRACT.economics.totalPotential - CONTRACT.economics.guaranteed) / 1000}
                                format={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }} suffix="K" /> : "$30K"}
                        </div>
                    </div>
                </div>
                <div className="h-24">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={CONTRACT.economics.areaData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="endGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#547bb5" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="#6e53b8" stopOpacity={0}   />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.06)", strokeWidth: 1 }} />
                            <Area type="monotone" dataKey="value" stroke="#547bb5" strokeWidth={2} fill="url(#endGrad)"
                                dot={false} isAnimationActive animationDuration={1400} animationEasing="ease-out" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-1 text-center text-[10px] text-white/25">Cumulative value over campaign</div>
            </div>
        </CardShell>
    );
}

function MoneyBack({ onFlip }: { onFlip?: () => void }) {
    return (
        <CardShell>
            <CardHeader title="Payment Schedule" icon={<DollarSign className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Back" />} />
            <div className="px-5 py-4 space-y-2.5">
                {CONTRACT.economics.paymentSchedule.map((item, i) => (
                    <motion.div key={item.label}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-white/5 bg-neutral-700 px-4 py-3"
                    >
                        <div>
                            <div className="text-sm font-medium text-white">{item.label}</div>
                            <div className="mt-0.5 flex items-center gap-1 text-xs text-white/35">
                                <Clock className="h-3 w-3" />{item.due}
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <div className="text-sm font-bold text-white">${(item.amount / 1000).toFixed(1)}K</div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TYPE_CLS[item.type]}`}>{item.type}</span>
                        </div>
                    </motion.div>
                ))}
            </div>
        </CardShell>
    );
}

// KEY DATES
function KeyDates() {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [barH, setBarH] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, 100, { duration: 1.1, ease: [0.22, 1, 0.36, 1], onUpdate: v => setBarH(v) });
        return () => ctrl.stop();
    }, [inView]);
    return (
        <CardShell>
            <CardHeader title="Key Dates" icon={<CalendarDays className="h-4 w-4" />} />
            <div className="px-5 py-4">
                <div ref={ref} className="relative">
                    <div className="absolute bottom-0 left-[19px] top-0 w-[2px] rounded-full bg-white/6">
                        <div className="w-full rounded-full bg-gradient-to-b from-[#547bb5] to-[#6e53b8]"
                            style={{ height: `${barH}%`, transition: "height 0s" }} />
                    </div>
                    <div className="space-y-5 pl-10">
                        {CONTRACT.term.keyDates.map((d, i) => (
                            <motion.div key={d.label}
                                initial={{ opacity: 0, x: -8 }} animate={inView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.15 + i * 0.1 }} className="relative"
                            >
                                <motion.div
                                    initial={{ scale: 0 }} animate={inView ? { scale: 1 } : {}}
                                    transition={{ type: "spring" as const, stiffness: 400, damping: 20, delay: 0.2 + i * 0.1 }}
                                    className="absolute -left-10 flex h-9 w-9 items-center justify-center rounded-full border-2 border-neutral-800 bg-gradient-to-br from-[#547bb5] to-[#6e53b8] text-white shadow-lg shadow-[#547bb5]/20"
                                >
                                    {ICON_MAP[d.icon]}
                                </motion.div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-white/35">{d.label}</div>
                                <div className="mt-0.5 text-[15px] font-bold text-white">{d.date}</div>
                                <div className="mt-0.5 text-xs text-white/40">{d.note}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
                <Divider />
                <DataRow label="Duration"        value={CONTRACT.term.duration} />
                <DataRow label="Renewal Options" value={CONTRACT.term.renewalOptions} />
                <DataRow label="Notice Period"   value={CONTRACT.term.noticePeriod} />
            </div>
        </CardShell>
    );
}

// RIGHTS CARD
function RightsFront({ onFlip }: { onFlip?: () => void }) {
    return (
        <CardShell>
            <CardHeader title="Rights & Usage" icon={<Megaphone className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Channels" />} />
            <div className="px-5 py-4">
                <DataRow icon={<Globe className="h-4 w-4" />}  label="Territory"      value={CONTRACT.rights.territory} />
                <DataRow icon={<Lock className="h-4 w-4" />}   label="Exclusivity"    value={CONTRACT.rights.exclusivity} />
                <DataRow icon={<Timer className="h-4 w-4" />}  label="Post-Term Use"  value={CONTRACT.rights.postTermUsage} />
                <DataRow icon={<Eye className="h-4 w-4" />}    label="Approvals"      value={CONTRACT.rights.approvalProcess} />
                <Divider />
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/35">Licensed Channels</div>
                <div className="flex flex-wrap gap-2">
                    {CONTRACT.rights.channels.map(ch => (
                        <div key={ch} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60">
                            <Wifi className="h-3 w-3" />{ch}
                        </div>
                    ))}
                </div>
            </div>
        </CardShell>
    );
}

function RightsBack({ onFlip }: { onFlip?: () => void }) {
    return (
        <CardShell>
            <CardHeader title="Channel Breakdown" icon={<Wifi className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Back" />} />
            <div className="px-5 py-4 space-y-2.5">
                {CONTRACT.rights.channelBreakdown.map((ch, i) => (
                    <motion.div key={ch.channel}
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                        className="rounded-2xl border border-white/5 bg-neutral-700 p-3.5"
                    >
                        <div className="mb-1 flex items-center justify-between">
                            <span className="text-sm font-semibold text-white">{ch.channel}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ch.paid ? "bg-blue-500/15 text-blue-300" : "bg-white/8 text-white/40"}`}>
                                {ch.paid ? "Paid Promo OK" : "Organic Only"}
                            </span>
                        </div>
                        <div className="text-xs text-white/45">{ch.rights}</div>
                    </motion.div>
                ))}
            </div>
        </CardShell>
    );
}

// OBLIGATIONS
function ObligationsCard() {
    const [expanded, setExpanded] = useState<number | null>(null);
    return (
        <CardShell>
            <CardHeader title="What You're Responsible For" icon={<FilePenLine className="h-4 w-4" />} />
            <div className="px-5 py-4 space-y-2">
                {CONTRACT.obligations.map((ob, i) => (
                    <div key={ob.deliverable} className="overflow-hidden rounded-2xl border border-white/5 bg-neutral-700">
                        <button onClick={() => setExpanded(expanded === i ? null : i)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/8 text-white/50">
                                {ICON_MAP[ob.icon]}
                            </div>
                            <span className="flex-1 text-sm font-medium text-white">{ob.deliverable}</span>
                            <motion.div animate={{ rotate: expanded === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <ChevronDown className="h-4 w-4 text-white/30" />
                            </motion.div>
                        </button>
                        <AnimatePresence>
                            {expanded === i && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <div className="border-t border-white/5 px-4 py-3">
                                        <div className="flex items-center gap-1.5 text-xs text-white/40">
                                            <Clock className="h-3 w-3" />
                                            <span className="font-semibold text-white/60">{ob.due}</span>
                                        </div>
                                        <div className="mt-1.5 text-xs text-white/40">{ob.note}</div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </CardShell>
    );
}

// CLAUSES
function ClausesCard() {
    const [open, setOpen] = useState<number | null>(null);
    return (
        <CardShell>
            <CardHeader title="Key Clauses" icon={<AlertTriangle className="h-4 w-4" />} />
            <div className="px-5 py-4 space-y-2">
                {CONTRACT.clauses.map((clause, i) => (
                    <div key={clause.title} className="overflow-hidden rounded-2xl border border-white/5 bg-neutral-700">
                        <button onClick={() => setOpen(open === i ? null : i)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors">
                            <div className="shrink-0">
                                {clause.flag ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : <FileText className="h-4 w-4 text-white/40" />}
                            </div>
                            <span className="flex-1 text-sm font-medium text-white">{clause.title}</span>
                            <motion.div animate={{ rotate: open === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <ChevronDown className="h-4 w-4 text-white/30" />
                            </motion.div>
                        </button>
                        <AnimatePresence>
                            {open === i && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <div className="border-t border-white/5 px-4 py-4">
                                        <p className="text-sm leading-6 text-white/55">{clause.summary}</p>
                                        {clause.flag && (
                                            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/15 bg-amber-500/8 px-3 py-2.5 text-xs text-amber-300">
                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{clause.flag}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </CardShell>
    );
}

// REDLINE HISTORY
function RedlineCard() {
    return (
        <CardShell>
            <CardHeader title="Redline History" icon={<TrendingUp className="h-4 w-4" />} />
            <div className="px-5 pb-4">
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs text-white/35">{CONTRACT.redlineHistory.length} changes · newest first</span>
                    <span className="rounded-full bg-[#547bb5]/20 px-2.5 py-0.5 text-[10px] font-bold text-[#9bbde0]">Live</span>
                </div>
                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1"
                    style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>
                    {CONTRACT.redlineHistory.map((entry, i) => {
                        const DI = entry.positive ? ArrowUpRight : ArrowDownRight;
                        return (
                            <motion.div key={i}
                                initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                                className="rounded-2xl border border-white/5 bg-neutral-700 p-3.5"
                            >
                                <div className="mb-2 flex items-center gap-2">
                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#547bb5] to-[#6e53b8] text-[9px] font-black text-white">
                                        {entry.initials}
                                    </div>
                                    <span className="text-xs font-semibold text-white/60">{entry.author}</span>
                                    <span className="ml-auto flex items-center gap-1 text-[10px] text-white/25">
                                        <Clock className="h-2.5 w-2.5" />{entry.timestamp}
                                    </span>
                                </div>
                                <div className="mb-1.5 text-xs font-bold uppercase tracking-widest text-white/35">{entry.clause}</div>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm text-white/35 line-through">{entry.from}</span>
                                        <Minus className="h-3 w-3 text-white/20" />
                                        <span className="text-sm font-semibold text-white">{entry.to}</span>
                                    </div>
                                    <span className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${entry.positive ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/12 text-rose-300"}`}>
                                        <DI className="h-3 w-3" />{entry.delta}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────

export default function EndorsementCardsDemo() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    return (
        <div className="w-full text-white">
            <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-2 gap-4 items-start">
                <motion.div variants={fadeUp}>
                    <FlipCard front={<MoneyFront mounted={mounted} />} back={<MoneyBack />} />
                </motion.div>
                <motion.div variants={fadeUp}><KeyDates /></motion.div>
                <motion.div variants={fadeUp}>
                    <FlipCard front={<RightsFront />} back={<RightsBack />} />
                </motion.div>
                <motion.div variants={fadeUp}><ObligationsCard /></motion.div>
                <motion.div variants={fadeUp}><ClausesCard /></motion.div>
                <motion.div variants={fadeUp}><RedlineCard /></motion.div>
            </motion.div>
        </div>
    );
}
